import { useState } from 'react';
import { Typography, Table, Switch, Button, Modal, Form, Input, Tag, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { getFeatureFlags, createFeatureFlag, updateFeatureFlag } from '@/services/featureFlags';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface FeatureFlag {
  id: string;
  key: string;
  description: string | null;
  enabled: boolean;
  updatedAt: string;
}

export default function FeatureFlagsPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const { data, isLoading } = useQuery({ queryKey: ['featureFlags'], queryFn: getFeatureFlags });

  const createMutation = useMutation({
    mutationFn: createFeatureFlag,
    onSuccess: () => { message.success('Feature flag created'); queryClient.invalidateQueries({ queryKey: ['featureFlags'] }); setCreateOpen(false); form.resetFields(); },
    onError: () => message.error('Failed to create flag'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => updateFeatureFlag(id, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['featureFlags'] }),
    onError: () => message.error('Failed to update flag'),
  });

  const flags: FeatureFlag[] = (data as { data?: FeatureFlag[] })?.data ?? (Array.isArray(data) ? data as FeatureFlag[] : []);

  const columns = [
    {
      title: 'Key', dataIndex: 'key', key: 'key',
      render: (v: string) => <code style={{ fontSize: 12, background: '#f8fafc', padding: '2px 6px', borderRadius: 4 }}>{v}</code>,
    },
    { title: 'Description', dataIndex: 'description', key: 'description', render: (v: string | null) => <span style={{ color: '#64748b' }}>{v ?? '—'}</span> },
    {
      title: 'Enabled', key: 'enabled',
      render: (_: unknown, record: FeatureFlag) => (
        <Switch checked={record.enabled} onChange={(checked) => toggleMutation.mutate({ id: record.id, enabled: checked })} />
      ),
    },
    {
      title: 'Updated At', dataIndex: 'updatedAt', key: 'updatedAt',
      render: (v: string) => <span style={{ color: '#94a3b8', fontSize: 12 }}>{dayjs(v).format('YYYY-MM-DD HH:mm')}</span>,
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <Typography.Title level={4} style={{ margin: 0, color: '#0f172a' }}>Feature Flags</Typography.Title>
            <Tag color="red" style={{ borderRadius: 20, fontWeight: 700 }}>Super Admin</Tag>
          </div>
          <Typography.Text style={{ color: '#64748b', fontSize: 13 }}>Toggle platform feature flags</Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>Create Flag</Button>
      </div>

      {isMobile ? (
        /* ── Mobile card list ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {isLoading ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
          ) : flags.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>No feature flags found</div>
          ) : flags.map((record) => (
            <div key={record.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                <code style={{ fontSize: 13, background: '#f8fafc', padding: '3px 8px', borderRadius: 6, color: '#6366f1', fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {record.key}
                </code>
                <Switch
                  checked={record.enabled}
                  onChange={(checked) => toggleMutation.mutate({ id: record.id, enabled: checked })}
                />
              </div>
              {record.description && (
                <div style={{ color: '#64748b', fontSize: 13, marginBottom: 8 }}>{record.description}</div>
              )}
              <div style={{ color: '#94a3b8', fontSize: 12 }}>
                Updated {dayjs(record.updatedAt).format('YYYY-MM-DD HH:mm')}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── Desktop table ── */
        <Table
          columns={columns}
          dataSource={flags}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 'max-content' }}
          style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}
        />
      )}

      <Modal title="Create Feature Flag" open={createOpen} onCancel={() => setCreateOpen(false)} onOk={() => form.submit()} confirmLoading={createMutation.isPending}>
        <Form form={form} layout="vertical" onFinish={(values) => createMutation.mutate(values)}>
          <Form.Item name="key" label="Key" rules={[{ required: true }]}><Input placeholder="e.g. enable_dark_mode" /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="enabled" label="Enabled" valuePropName="checked" initialValue={false}><Switch /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
